import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Button, Card, Col, Dropdown, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import { FieldLabel, StudentPicker } from '../discipline/leaveOptions';
import {
  EXIT_OUTCOMES, HealthAlert, LEAVE_OUTCOMES, OUTCOMES, SOURCES,
  nowHHMM, outcomeBadge, stayText, thaiDate, timeOf, todayISO,
} from './nurseOptions';

const alertError = (title, err) =>
  Swal.fire({ icon: 'error', title, text: err?.response?.data?.message || '' });

const emptyForm = () => ({
  date: todayISO(),
  checkInTime: nowHHMM(),
  source: 'walk_in',
  symptomId: '',
  symptomDetail: '',
  temp: '',
  pulse: '',
  bpSys: '',
  bpDia: '',
  spo2: '',
  treatment: '',
  outcome: 'back_to_class',
  referPlace: '',
  note: '',
  leaveEnd: todayISO(),
  // ใบผ่านประตู — เปิดเมื่อผลลัพธ์เป็นการออกนอกโรงเรียน
  issuePass: true,
  passType: 'outing',
  outTime: nowHHMM(),
  expectReturnTime: '18:00',
  returnDate: '',
  guardianName: '',
  guardianPhone: '',
});

export default function NurseVisit() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [keyword, setKeyword] = useState('');
  const [students, setStudents] = useState([]);
  const [searchedTerm, setSearchedTerm] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selected, setSelected] = useState(null);

  const [symptoms, setSymptoms] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [openVisits, setOpenVisits] = useState([]);

  // ยาที่จ่าย — ตัดสต๊อกจริงหลังบันทึก visit เสร็จ เพราะ txn ต้องผูกกับ nv_id
  const [medList, setMedList] = useState([]);
  const [meds, setMeds] = useState([]);
  const [medPick, setMedPick] = useState({ itemId: '', qty: '' });

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

  useEffect(() => {
    axiosInstance.get('/api/nurse/symptoms')
      .then(res => setSymptoms(res.data || []))
      .catch(() => setSymptoms([]));
    axiosInstance.get('/api/nurse/medicines')
      .then(res => setMedList(res.data || []))
      .catch(() => setMedList([]));
  }, []);

  // คนที่ยังอยู่ในห้องพยาบาลตอนนี้ — พยาบาลต้องเห็นตลอดเวลาที่กรอกคนใหม่
  const fetchOpen = useCallback(() => {
    if (!yearId) return;
    axiosInstance.get(`/api/nurse/visits?yearId=${yearId}&open=1&limit=50`)
      .then(res => setOpenVisits(res.data || []))
      .catch(() => setOpenVisits([]));
  }, [yearId]);

  useEffect(() => { fetchOpen(); }, [fetchOpen]);

  useEffect(() => {
    setSelected(null);
    setStudents([]);
    setSearchedTerm('');
  }, [yearId]);

  const searchStudents = useCallback((e) => {
    e?.preventDefault();
    const term = keyword.trim();
    if (!yearId) return;
    if (!term) {
      Swal.fire({ icon: 'info', title: 'กรุณาป้อนเลขประจำตัวหรือชื่อนักเรียน', timer: 1600, showConfirmButton: false });
      return;
    }
    setLoadingStudents(true);
    axiosInstance.get(`/api/nurse/students/search?yearId=${yearId}&keyword=${encodeURIComponent(term)}`)
      .then(res => {
        setStudents(res.data || []);
        setSearchedTerm(term);
      })
      .catch(err => alertError('ไม่สามารถค้นหานักเรียนได้', err))
      .finally(() => setLoadingStudents(false));
  }, [yearId, keyword]);

  // เลือกนักเรียนแล้วดึงประวัติมาห้องพยาบาลทันที — "มาอีกแล้วเหรอ" คือคำถามแรกเสมอ
  const selectStudent = (student) => {
    setSelected(student);
    axiosInstance.get(`/api/nurse/visits?yearId=${yearId}&studentId=${student.st_id}&limit=10`)
      .then(res => setHistory(res.data || []))
      .catch(() => setHistory([]));
  };

  const clearAll = () => {
    setSelected(null);
    setHistory([]);
    setForm(emptyForm());
    setMeds([]);
    setMedPick({ itemId: '', qty: '' });
  };

  const addMed = () => {
    const item = medList.find(m => String(m.mi_id) === String(medPick.itemId));
    const amount = Number(medPick.qty);
    if (!item || !(amount > 0)) return;
    setMeds(prev => [
      ...prev.filter(m => m.itemId !== item.mi_id),
      { itemId: item.mi_id, name: item.mi_name, unit: item.mi_unit, qty: amount, stock: Number(item.stock) },
    ]);
    setMedPick({ itemId: '', qty: '' });
  };

  const needLeave = LEAVE_OUTCOMES.includes(form.outcome);
  const needPass = EXIT_OUTCOMES.includes(form.outcome);

  // เปลี่ยนผลลัพธ์แล้วตั้งค่าเริ่มต้นของส่วนที่เพิ่งเปิดให้ตรงกับเรื่อง
  const selectOutcome = (value) => {
    const patch = { outcome: value };
    if (value === 'send_home') patch.passType = 'home_leave';
    if (value === 'refer_hospital') patch.passType = 'outing';
    set(patch);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!form.symptomId && !form.symptomDetail.trim()) {
      Swal.fire({ icon: 'info', title: 'กรุณาระบุอาการ', text: 'เลือกจากรายการ หรือพิมพ์อาการเพิ่มเติม' });
      return;
    }
    if (needPass && form.issuePass && !form.referPlace.trim() && form.passType === 'outing') {
      Swal.fire({ icon: 'info', title: 'กรุณาระบุปลายทาง', text: 'ชื่อโรงพยาบาลหรือสถานที่ที่ส่งตัวไป' });
      return;
    }

    const symptomName = symptoms.find(s => String(s.ns_id) === String(form.symptomId))?.ns_name || '';
    setSaving(true);
    axiosInstance.post('/api/nurse/visits', {
      studentId: selected.st_id,
      studentName: selected.st_name,
      yearId,
      date: form.date,
      checkInTime: form.checkInTime,
      source: form.source,
      symptomId: form.symptomId || null,
      symptomName,
      symptomDetail: form.symptomDetail.trim(),
      temp: form.temp,
      pulse: form.pulse,
      bpSys: form.bpSys,
      bpDia: form.bpDia,
      spo2: form.spo2,
      treatment: form.treatment.trim(),
      outcome: form.outcome,
      referPlace: form.referPlace.trim(),
      note: form.note.trim(),
      leaveEnd: needLeave ? (form.leaveEnd || form.date) : null,
      referral: needPass && form.issuePass ? {
        type: form.passType,
        outTime: form.outTime,
        expectReturnTime: form.expectReturnTime,
        returnDate: form.returnDate || null,
        destination: form.referPlace.trim(),
        guardianName: form.guardianName.trim(),
        guardianPhone: form.guardianPhone.trim(),
      } : null,
    }).then(async ({ data }) => {
      const lines = [];
      if (meds.length) {
        try {
          const res = await axiosInstance.post('/api/nurse/medicines/dispense', {
            nvId: data.nvId,
            items: meds.map(m => ({ itemId: m.itemId, qty: m.qty })),
            reason: symptomName || null,
          });
          lines.push(`ตัดยาออกจากคลัง ${meds.length} รายการ`);
          if (res.data.shortNote) lines.push(res.data.shortNote);
        } catch {
          // บันทึกการมาห้องพยาบาลสำเร็จไปแล้ว การตัดสต๊อกพลาดต้องไม่ทำให้ดูเหมือนล้มทั้งหมด
          lines.push('บันทึกแล้ว แต่ตัดยาออกจากคลังไม่สำเร็จ — ตรวจสอบที่หน้าคลังยา');
        }
      }
      if (data.leaveId) lines.push('แจ้งลาป่วยให้อัตโนมัติแล้ว');
      if (data.leaveNote) lines.push(data.leaveNote);
      if (data.refId) {
        lines.push(data.refType === 'home_leave'
          ? 'ออกใบกลับบ้านแล้ว — ยามเห็นที่ประตู'
          : 'ออกใบส่งตัวแล้ว — ยามเห็นที่ประตู');
      }
      Swal.fire({
        icon: 'success',
        title: 'บันทึกแล้ว',
        html: `<div class="fw-semibold">${selected.st_name}</div>`
          + (lines.length ? `<div class="fs-sm mt-2">${lines.join('<br/>')}</div>` : ''),
        showCancelButton: !!data.refId,
        confirmButtonText: 'เสร็จสิ้น',
        cancelButtonText: 'พิมพ์ใบส่งตัว',
        reverseButtons: true,
      }).then(result => {
        if (data.refId && result.dismiss === Swal.DismissReason.cancel) {
          window.open(`/nurse/visits/${data.nvId}/referral`, '_blank');
        }
      });
      clearAll();
      fetchOpen();
    }).catch(err => alertError('ไม่สามารถบันทึกได้', err))
      .finally(() => setSaving(false));
  };

  const checkout = (visit) => {
    axiosInstance.post(`/api/nurse/visits/${visit.nv_id}/checkout`, {})
      .then(() => {
        fetchOpen();
        Swal.fire({ icon: 'success', title: 'ปิดเวลาออกแล้ว', text: visit.st_name, timer: 1400, showConfirmButton: false });
      })
      .catch(err => alertError('ไม่สามารถปิดเวลาออกได้', err));
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ห้องพยาบาล</li>
              <li className="breadcrumb-item active">บันทึกการมาห้องพยาบาล</li>
            </ol>
            <h4 className="main-title mb-0">บันทึกการมาห้องพยาบาล</h4>
            <div className="fs-xs text-secondary">
              ผลลัพธ์ที่ทำให้ไม่ได้อยู่ในห้องเรียน ระบบจะแจ้งลาป่วยและแจ้งครูประจำชั้นให้เอง
            </div>
          </div>
          <Dropdown className="mt-3 mt-md-0">
            <Dropdown.Toggle variant="primary" id="nurse-visit-year">
              <i className="ri-calendar-line me-1"></i>{yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(year => (
                <Dropdown.Item key={year.year_id} onClick={() => handleSelectYear(year.year_id)}>
                  {`ปีการศึกษา ${year.year_year}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <Row className="g-3">
          <Col xl={7}>
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">บันทึกใหม่</Card.Title>
                {selected && (
                  <Button variant="link" size="sm" className="p-0 ms-auto" onClick={clearAll}>ล้างฟอร์ม</Button>
                )}
              </Card.Header>
              <Card.Body>
                <StudentPicker
                  keyword={keyword}
                  onKeywordChange={setKeyword}
                  onSubmit={searchStudents}
                  students={students}
                  loading={loadingStudents}
                  selected={selected}
                  onSelect={selectStudent}
                  searchedTerm={searchedTerm}
                  onClear={clearAll}
                />

                {!selected ? (
                  <div className="text-center text-secondary fs-xs border-top pt-3 mt-1">
                    ค้นหาและเลือกนักเรียนก่อน แล้วฟอร์มบันทึกจะปรากฏที่นี่
                  </div>
                ) : (
                  <>
                    <HealthAlert student={selected} />

                    <Form onSubmit={submit}>
                      <Row className="g-3">
                        <Col sm={4}>
                          <FieldLabel required>วันที่</FieldLabel>
                          <Form.Control type="date" value={form.date}
                            onChange={e => set({ date: e.target.value, leaveEnd: e.target.value })} required />
                        </Col>
                        <Col sm={3}>
                          <FieldLabel required>เวลาเข้า</FieldLabel>
                          <Form.Control type="time" value={form.checkInTime}
                            onChange={e => set({ checkInTime: e.target.value })} required />
                        </Col>
                        <Col sm={5}>
                          <FieldLabel>มาอย่างไร</FieldLabel>
                          <Form.Select value={form.source} onChange={e => set({ source: e.target.value })}>
                            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </Form.Select>
                        </Col>

                        <Col sm={5}>
                          <FieldLabel required hint="ใช้ทำสถิติและเฝ้าระวังการระบาด">อาการหลัก</FieldLabel>
                          <Form.Select value={form.symptomId} onChange={e => set({ symptomId: e.target.value })}>
                            <option value="">— เลือกอาการ —</option>
                            {symptoms.map(s => (
                              <option key={s.ns_id} value={s.ns_id}>{s.ns_name}</option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col sm={7}>
                          <FieldLabel>รายละเอียดอาการ</FieldLabel>
                          <Form.Control value={form.symptomDetail} maxLength={255}
                            onChange={e => set({ symptomDetail: e.target.value })}
                            placeholder="เช่น ไข้มาตั้งแต่เมื่อคืน ปวดหัวร่วมด้วย" />
                        </Col>

                        <Col xs={12}>
                          <div className="border rounded p-2">
                            <div className="fs-xs text-secondary mb-2">สัญญาณชีพ (เว้นว่างได้ถ้าไม่ได้วัด)</div>
                            <Row className="g-2">
                              <Col xs={6} md={3}>
                                <FieldLabel>อุณหภูมิ (°C)</FieldLabel>
                                <Form.Control type="number" step="0.1" min="30" max="45" inputMode="decimal"
                                  value={form.temp} onChange={e => set({ temp: e.target.value })} />
                              </Col>
                              <Col xs={6} md={3}>
                                <FieldLabel>ชีพจร (/นาที)</FieldLabel>
                                <Form.Control type="number" min="20" max="250" inputMode="numeric"
                                  value={form.pulse} onChange={e => set({ pulse: e.target.value })} />
                              </Col>
                              <Col xs={6} md={4}>
                                <FieldLabel>ความดัน</FieldLabel>
                                <div className="d-flex align-items-center gap-1">
                                  <Form.Control type="number" min="40" max="260" inputMode="numeric" placeholder="120"
                                    value={form.bpSys} onChange={e => set({ bpSys: e.target.value })} />
                                  <span>/</span>
                                  <Form.Control type="number" min="20" max="200" inputMode="numeric" placeholder="80"
                                    value={form.bpDia} onChange={e => set({ bpDia: e.target.value })} />
                                </div>
                              </Col>
                              <Col xs={6} md={2}>
                                <FieldLabel>SpO₂ (%)</FieldLabel>
                                <Form.Control type="number" min="50" max="100" inputMode="numeric"
                                  value={form.spo2} onChange={e => set({ spo2: e.target.value })} />
                              </Col>
                            </Row>
                          </div>
                        </Col>

                        <Col xs={12}>
                          <FieldLabel>การรักษา / คำแนะนำ</FieldLabel>
                          <Form.Control as="textarea" rows={2} value={form.treatment}
                            onChange={e => set({ treatment: e.target.value })}
                            placeholder="ยาที่จ่าย วิธีดูแล และสิ่งที่บอกนักเรียน" />
                        </Col>

                        <Col xs={12}>
                          <FieldLabel required>ผลลัพธ์</FieldLabel>
                          <div className="d-flex flex-wrap gap-2">
                            {OUTCOMES.map(o => (
                              <Button key={o.value}
                                type="button"
                                variant={form.outcome === o.value ? o.variant : `outline-${o.variant}`}
                                size="sm"
                                onClick={() => selectOutcome(o.value)}>
                                <i className={`${o.icon} me-1`}></i>{o.label}
                              </Button>
                            ))}
                          </div>
                        </Col>

                        {needLeave && (
                          <Col xs={12}>
                            <div className="alert alert-warning py-2 mb-0">
                              <div className="fw-semibold fs-sm mb-2">
                                <i className="ri-calendar-check-line me-1"></i>
                                ระบบจะแจ้งลาป่วยให้อัตโนมัติ — ครูประจำชั้นไม่ต้องแจ้งซ้ำ
                              </div>
                              <Row className="g-2 align-items-end">
                                <Col sm={5}>
                                  <FieldLabel hint="วันสุดท้ายที่ลา">ลาถึงวันที่</FieldLabel>
                                  <Form.Control type="date" size="sm" value={form.leaveEnd} min={form.date}
                                    onChange={e => set({ leaveEnd: e.target.value })} />
                                </Col>
                                <Col sm={7} className="fs-xs text-secondary">
                                  ถ้ามีใบลาช่วงนี้อยู่แล้ว ระบบจะข้ามให้ ไม่แจ้งซ้ำ
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        )}

                        {needPass && (
                          <Col xs={12}>
                            <div className="border border-danger rounded p-2">
                              <Form.Check
                                type="switch"
                                id="nurse-issue-pass"
                                className="fw-semibold"
                                label="ออกใบผ่านประตูให้เลย (อนุมัติทันที ยามเห็นที่ประตู)"
                                checked={form.issuePass}
                                onChange={e => set({ issuePass: e.target.checked })}
                              />
                              {form.issuePass && (
                                <Row className="g-2 mt-1">
                                  <Col sm={5}>
                                    <FieldLabel>ประเภทใบ</FieldLabel>
                                    <Form.Select size="sm" value={form.passType}
                                      onChange={e => set({ passType: e.target.value })}>
                                      <option value="outing">ใบส่งตัว ไป-กลับวันเดียว (โรงพยาบาล)</option>
                                      <option value="home_leave">ใบกลับบ้าน (ผู้ปกครองรับไปพัก)</option>
                                    </Form.Select>
                                  </Col>
                                  <Col sm={7}>
                                    <FieldLabel required={form.passType === 'outing'}>ปลายทาง</FieldLabel>
                                    <Form.Control size="sm" value={form.referPlace} maxLength={255}
                                      onChange={e => set({ referPlace: e.target.value })}
                                      placeholder="เช่น โรงพยาบาลนราธิวาสราชนครินทร์" />
                                  </Col>
                                  {form.passType === 'outing' ? (
                                    <>
                                      <Col xs={6} sm={3}>
                                        <FieldLabel>เวลาออก</FieldLabel>
                                        <Form.Control type="time" size="sm" value={form.outTime}
                                          onChange={e => set({ outTime: e.target.value })} />
                                      </Col>
                                      <Col xs={6} sm={3}>
                                        <FieldLabel hint="โดยประมาณ">เวลากลับ</FieldLabel>
                                        <Form.Control type="time" size="sm" value={form.expectReturnTime}
                                          onChange={e => set({ expectReturnTime: e.target.value })} />
                                      </Col>
                                    </>
                                  ) : (
                                    <Col xs={12} sm={6}>
                                      <FieldLabel hint="เว้นว่างได้ถ้ายังไม่รู้">กลับเข้าโรงเรียนวันที่</FieldLabel>
                                      <Form.Control type="date" size="sm" value={form.returnDate} min={form.date}
                                        onChange={e => set({ returnDate: e.target.value })} />
                                    </Col>
                                  )}
                                  <Col sm={form.passType === 'outing' ? 3 : 3}>
                                    <FieldLabel>ผู้รับตัว</FieldLabel>
                                    <Form.Control size="sm" value={form.guardianName}
                                      onChange={e => set({ guardianName: e.target.value })}
                                      placeholder="ชื่อผู้ปกครอง/ครู" />
                                  </Col>
                                  <Col sm={3}>
                                    <FieldLabel>เบอร์โทร</FieldLabel>
                                    <Form.Control size="sm" value={form.guardianPhone} inputMode="tel"
                                      onChange={e => set({ guardianPhone: e.target.value })} />
                                  </Col>
                                </Row>
                              )}
                            </div>
                          </Col>
                        )}

                        <Col xs={12}>
                          <div className="border rounded p-2">
                            <div className="fs-xs text-secondary mb-2">
                              ยาที่จ่าย (ตัดสต๊อกอัตโนมัติจากล็อตที่หมดอายุก่อน)
                            </div>
                            {meds.length > 0 && (
                              <Table size="sm" className="mb-2 fs-sm">
                                <tbody>
                                  {meds.map(m => (
                                    <tr key={m.itemId}>
                                      <td>{m.name}</td>
                                      <td className="text-end" style={{ width: 110 }}>
                                        {m.qty} {m.unit}
                                        {m.qty > m.stock && (
                                          <div className="fs-xs text-danger">คลังเหลือ {m.stock}</div>
                                        )}
                                      </td>
                                      <td className="text-end" style={{ width: 40 }}>
                                        <Button size="sm" variant="link" className="text-danger p-0"
                                          onClick={() => setMeds(meds.filter(x => x.itemId !== m.itemId))}
                                          aria-label="เอาออก">
                                          <i className="ri-close-line"></i>
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            )}
                            <Row className="g-2">
                              <Col xs={7}>
                                <Form.Select size="sm" value={medPick.itemId}
                                  onChange={e => setMedPick({ ...medPick, itemId: e.target.value })}>
                                  <option value="">— เลือกยา —</option>
                                  {medList.map(m => (
                                    <option key={m.mi_id} value={m.mi_id}>
                                      {m.mi_name} (เหลือ {Number(m.stock)} {m.mi_unit})
                                    </option>
                                  ))}
                                </Form.Select>
                              </Col>
                              <Col xs={3}>
                                <Form.Control size="sm" type="number" min="0.01" step="0.01"
                                  placeholder="จำนวน" value={medPick.qty}
                                  onChange={e => setMedPick({ ...medPick, qty: e.target.value })} />
                              </Col>
                              <Col xs={2} className="d-grid">
                                <Button size="sm" variant="outline-primary" type="button" onClick={addMed}>
                                  <i className="ri-add-line"></i>
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        </Col>

                        <Col xs={12}>
                          <FieldLabel>หมายเหตุ</FieldLabel>
                          <Form.Control value={form.note} maxLength={255}
                            onChange={e => set({ note: e.target.value })} />
                        </Col>

                        <Col xs={12} className="d-grid">
                          <Button type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : (
                              <><i className="ri-save-line me-1"></i>บันทึก</>
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form>

                    {history.length > 0 && (
                      <div className="border-top mt-3 pt-3">
                        <div className="fw-semibold fs-sm mb-2">
                          มาห้องพยาบาลล่าสุด {history.length} ครั้ง
                        </div>
                        <Table size="sm" className="mb-0 fs-xs">
                          <tbody>
                            {history.map(h => (
                              <tr key={h.nv_id}>
                                <td style={{ width: 90 }}>{thaiDate(h.nv_date)}</td>
                                <td>{h.ns_name || h.nv_symptom_detail || '-'}</td>
                                <td className="text-end">{outcomeBadge(h.nv_outcome)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={5}>
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">
                  ยังอยู่ในห้องพยาบาล
                  <span className="badge bg-warning ms-2">{openVisits.length}</span>
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                {openVisits.length === 0 ? (
                  <div className="text-center text-secondary py-4 fs-sm">ไม่มีนักเรียนค้างอยู่ในห้องพยาบาล</div>
                ) : (
                  <Table hover className="mb-0 fs-sm">
                    <tbody>
                      {openVisits.map(v => (
                        <tr key={v.nv_id}>
                          <td>
                            <Link to={`/nurse/students/${v.st_id}`} className="fw-semibold">{v.st_name}</Link>
                            <div className="fs-xs text-secondary">
                              ม.{v.c_id}/{v.r_no} · เข้า {timeOf(v.nv_check_in)} น. ({stayText(v.nv_check_in)})
                            </div>
                            <div className="fs-xs">
                              {v.ns_name || v.nv_symptom_detail || '-'}
                              {v.nv_temp ? ` · ${v.nv_temp}°C` : ''}
                              <span className="ms-1">{outcomeBadge(v.nv_outcome)}</span>
                            </div>
                          </td>
                          <td className="text-end align-middle" style={{ width: 96 }}>
                            <Button size="sm" variant="outline-success" onClick={() => checkout(v)}>
                              <i className="ri-logout-box-r-line me-1"></i>ออก
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </>
  );
}
